import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DoCheck, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';

import { ReactiveFormsModule, Validators } from '@angular/forms';
import { NgLabelTemplateDirective, NgOptionTemplateDirective, NgSelectComponent } from '@ng-select/ng-select';
import { TranslateModule } from '@ngx-translate/core';
import { Utils } from '../../../../Utils/utils';
import { Subscription } from 'rxjs';
@Component({
  selector: 'app-date',
  imports: [ReactiveFormsModule, TranslateModule, NgSelectComponent, NgLabelTemplateDirective, NgOptionTemplateDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './date.component.html',
  styleUrl: './date.component.scss'
})
export class DateComponent implements OnChanges, OnInit, OnDestroy, DoCheck {
  @ViewChild('selectElement', { static: false })
  selectElementRef!: NgSelectComponent;
  @Input() allowedDate: 'future' | 'past' | 'all' = 'future';
  @Input() label: string = '';
  @Input() control: any;
  @Input() noOfPastYear: any = "";
  @Input() noOfPastMonth: any = "";
  @Input() noOfFutureMonth: any = "";
  @Input() disabledStartYear: number = 1900;
  @Input() futureYearCount: number | null = null;
  @Input() minYear: number | null = null;
  @Input() validationMessages: { [key: string]: string } = {};
  @Output() onDateChange = new EventEmitter<FocusEvent>();

  days: any[] = [];
  months: any[] = [];
  years: any[] = [];
  isDisabled: boolean = false;
  private effectiveAllowedDate: 'future' | 'past' | 'all' = 'future';
  private statusSubscription = new Subscription();
  private hideOcrDateOutOfRangeMessage: boolean = false;
  private previousOcrDateOutOfRangeState: boolean = false;
  private previousDisabledState: boolean | null = null;

  constructor(private cdr: ChangeDetectorRef) { }

  get isRequired(): boolean {
    const groupRequired = this.control?.hasValidator?.(Validators.required) || this.control?.hasValidator?.(Validators.requiredTrue);
    const dayRequired = this.controlDay?.hasValidator?.(Validators.required);
    const monthRequired = this.controlMonth?.hasValidator?.(Validators.required);
    const yearRequired = this.controlYear?.hasValidator?.(Validators.required);
    return Boolean(groupRequired || dayRequired || monthRequired || yearRequired);
  }
  // This method is used to handle the on init lifecycle hook
  ngOnInit(): void {
    this.bindControlStatusChanges();
    this.syncDisabledState();
    this.rebuildDateOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['control'] || changes['allowedDate'] || changes['noOfPastYear'] || changes['noOfPastMonth'] || changes['noOfFutureMonth']) {
      if (changes['control']) {
        this.bindControlStatusChanges();
      }
      this.syncDisabledState();
      this.rebuildDateOptions();
    }
  }

  ngOnDestroy(): void {
    this.statusSubscription.unsubscribe();
  }

  ngDoCheck(): void {
    const currentDisabledState = !!(this.control?.disabled || this.control?.get('year')?.disabled);
    if (this.previousDisabledState === null) {
      this.previousDisabledState = currentDisabledState;
      return;
    }

    if (this.previousDisabledState !== currentDisabledState) {
      this.previousDisabledState = currentDisabledState;
      this.syncDisabledState();
      this.rebuildDateOptions();
      this.cdr.markForCheck();
    }
  }

  private syncDisabledState(): void {
    const isExpiryDateControl = this.control?.parent?.get('expiryDate') === this.control;
    if (isExpiryDateControl) {
      this.isDisabled = false;
      this.effectiveAllowedDate = this.allowedDate;
      return;
    }
    this.isDisabled = (this.control?.disabled || this.control?.get('year')?.disabled);
    this.effectiveAllowedDate = this.isDisabled ? 'all' : this.allowedDate;
  }

  private rebuildDateOptions(): void {
    const initialMonth = this.control.get('month')?.value;
    const initialYear = this.control.get('year')?.value;
    const normalizedInitialYear = initialYear !== null && initialYear !== undefined && initialYear !== ''
      ? Number(initialYear)
      : null;
    this.days = Utils.getDates(initialMonth, initialYear, this.effectiveAllowedDate, this.isDisabled, this.noOfPastMonth, this.noOfFutureMonth);
    this.months = Utils.getMonths(this.noOfPastMonth, initialYear, this.effectiveAllowedDate, this.isDisabled, this.noOfFutureMonth);
    this.years = Utils.getYears(
      this.noOfPastYear,
      this.effectiveAllowedDate,
      this.isDisabled,
      this.disabledStartYear,
      this.futureYearCount,
      this.noOfPastMonth,
      this.noOfFutureMonth
    );
    if (this.minYear != null) {
      this.years = this.years.filter((yearItem: any) => Number(yearItem.value) >= Number(this.minYear));
    }
    if (this.effectiveAllowedDate === 'past' && this.noOfPastMonth && !this.isDisabled) {
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const startDate = new Date(currentYear, currentDate.getMonth() - this.noOfPastMonth, 1);
      const startYear = startDate.getFullYear();
      this.years = Array.from({ length: currentYear - startYear + 1 }, (_, i) => {
        const year = startYear + i;
        return { label: `${year}`, value: year };
      }).reverse();
    }
    //04AUG206 For the initial year, if it is not in the list of years, we need to add it to the list of years
    this.years = this.ensureSelectedOption(this.years, normalizedInitialYear, (value) => ({
      label: `${value}`,
      value,
    }));
    this.months = this.ensureSelectedOption(this.months, initialMonth, (value) => ({
      label: `${value}`.padStart(2, '0'),
      value: `${value}`.padStart(2, '0'),
    }));
    const initialDay = this.control.get('day')?.value;
    this.days = this.ensureSelectedOption(this.days, initialDay, (value) => ({
      label: `${value}`.padStart(2, '0'),
      value: `${value}`.padStart(2, '0'),
    }));
    //END

    const yearExists = this.years.some((yearItem) => Number(yearItem.value) == normalizedInitialYear);
    if (initialYear && yearExists && normalizedInitialYear !== initialYear) {
      this.control.get('year')?.setValue(normalizedInitialYear, { emitEvent: false });
    }
  }

  // This method is used to update months
  updateMonths() {
    const selectedYear = this.control.get('year')?.value;
    this.months = Utils.getMonths(this.noOfPastMonth, selectedYear, this.effectiveAllowedDate, this.isDisabled, this.noOfFutureMonth);
    const monthControl = this.control.get('month');
    const selectedMonth = monthControl?.value;
    const monthExist = this.months.some((month) => String(month.value) === String(selectedMonth));
    if (!monthExist) {
      monthControl?.setValue(null);
    }
    this.updateDays();
    // this.onDateChange.emit(this.control.get("month")?.value);
  }
  // This method is used to update days
  updateDays() {
    const month = this.control.get('month')?.value;
    const year = this.control.get('year')?.value;
    this.days = Utils.getDates(month, year, this.effectiveAllowedDate, this.isDisabled, this.noOfPastMonth, this.noOfFutureMonth);
    const dayControl = this.control.get('day');
    const selectedDay = dayControl?.value;
    const dayExist = this.days.some((day) => String(day.value) === String(selectedDay));
    if (!dayExist) {
      dayControl?.setValue(null);
    }
  }
  // This method is used to error keys
  get errorKeys(): string[] {
    return Object.keys(this.control?.errors || {});
  }

  get hasOcrDateOutOfRangeError(): boolean {
    return !!this.control?.errors?.['ocrDateOutOfRange']
      || !!this.validationMessages?.['ocrDateOutOfRange']
      && this.control.get('year')?.value == null
      && !this.hideOcrDateOutOfRangeMessage;
  }

  get ocrDateOutOfRangeMessage(): string {
    return this.validationMessages?.['ocrDateOutOfRange'] ?? '';
  }

  // This method is used to assess if show custom errors
  get shouldShowCustomErrors(): boolean { return !!this.control && (this.control.touched || this.control.dirty) && this.errorKeys.length > 0; }
  // This method is used to get custom error message using error key
  getCustomErrorMessage(errorKey: string): string | null {
    if (errorKey === 'ocrDateOutOfRange') {
      return null;
    }
    return this.validationMessages?.[errorKey] ?? null;
  }
  // This method is used to control day
  get controlDay() { return this.control.get('day'); }
  // This method is used to control month
  get controlMonth() { return this.control.get('month'); }
  // This method is used to control year
  get controlYear() { return this.control.get('year'); }

  // This method is used to mark touched
  markTouched() {
    this.hideOcrDateOutOfRangeMessage = true;
    this.control.markAllAsTouched();
    this.cdr.markForCheck();
  }
  // This method is used to focus
  focus() {
    if (this.selectElementRef) {
      this.selectElementRef.focus();
      this.selectElementRef.element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }
  // This method is used to check and emit date
  checkAndEmitDate() {
    this.hideOcrDateOutOfRangeMessage = true;
    const day = this.control.get('day')?.value;
    const month = this.control.get('month')?.value;
    const year = this.control.get('year')?.value;

    if (day && month && year) {
      let date: any = { day, month, year };
      this.onDateChange.emit(date);
    }
  }

  private bindControlStatusChanges(): void {
    this.statusSubscription.unsubscribe();
    this.statusSubscription = new Subscription();

    if (!this.control) {
      return;
    }

    const controlsToWatch = [
      this.control,
      this.control.get?.('year'),
      this.control.get?.('month'),
      this.control.get?.('day')
    ].filter((control) => !!control?.statusChanges);

    controlsToWatch.forEach((control) => {
      this.statusSubscription.add(
        control.statusChanges.subscribe(() => {
          this.syncDisabledState();
          this.rebuildDateOptions();
          this.syncOcrDateOutOfRangeVisibility();
          this.cdr.markForCheck();
        })
      );

      if (control.valueChanges) {
        this.statusSubscription.add(
          control.valueChanges.subscribe(() => {
            this.hideOcrDateOutOfRangeMessage = true;
            this.syncOcrDateOutOfRangeVisibility();
            this.cdr.markForCheck();
          })
        );
      }
    });
  }

  private syncOcrDateOutOfRangeVisibility(): void {
    const hasCurrentOcrDateOutOfRangeError = !!this.control?.errors?.['ocrDateOutOfRange'];
    if (hasCurrentOcrDateOutOfRangeError && !this.previousOcrDateOutOfRangeState) {
      this.hideOcrDateOutOfRangeMessage = false;
    }
    this.previousOcrDateOutOfRangeState = hasCurrentOcrDateOutOfRangeError;
  }

  //04AUG206 This method is used to ensure selected option
  private ensureSelectedOption<T>(
    items: T[],
    selectedValue: any,
    createOption: (value: any) => T
  ): T[] {
    if (selectedValue === null || selectedValue === undefined || selectedValue === '') {
      return items;
    }

    const exists = items.some((item: any) => String(item?.value) === String(selectedValue));
    if (exists) {
      return items;
    }

    return [createOption(selectedValue), ...items];
  }
}